import { useCallback } from 'react';
import { useAuth } from './useAuth';
import { chatService } from '../services/chatService';
import { logger } from '../utils/logger';

const moduleLogger = logger.getModule('LinkTracking');

/**
 * Custom hook for tracking link clicks in chat messages.
 * 
 * Encapsulates link click tracking logic, including session context retrieval
 * and non-blocking API calls.
 * 
 * @param messageId - Optional message ID to associate with the link click
 * @returns Function to track a link click (url, text)
 * 
 * @example
 * const trackLinkClick = useTrackLinkClick(message.messageId);
 * 
 * // In a link handler:
 * trackLinkClick(url, linkText);
 */
export const useTrackLinkClick = (messageId?: number) => {
  const { session } = useAuth();
  const sessionId = session?.sessionId;

  return useCallback(
    (url: string, text: string) => {
      if (!sessionId) {
        moduleLogger.debug('No session ID, skipping link tracking', { url });
        return;
      }

      const isExternal = !url.startsWith(window.location.origin);
      moduleLogger.debug('Tracking link click', { url, text, sessionId, messageId });

      // Track the click non-blocking
      chatService
        .trackLinkClick(sessionId, {
          url,
          text,
          is_external: isExternal,
          message_id: messageId
        })
        .catch((err) => {
          moduleLogger.error('Failed to track link click', err);
        });
    },
    [sessionId, messageId]
  );
};
